import { COLORS } from "./constants";

// TODO: rename NEXT_PUBLIC_TURNR_ENV to something without "TURNR" (e.g. NEXT_PUBLIC_ENV)
const rasputinPort = process.env.NEXT_PUBLIC_RASPUTIN_PORT || "5123";
const returnPortalPort = process.env.NEXT_PUBLIC_RETURN_PORTAL_PORT || "3001";

export const ENVIRONMENTS = {
    PROD: "PROD",
    STAGE: "STAGE",
    DEV: "DEV",
    LOCALHOST: "LOCALHOST",
};

export const HOSTS = {
    [ENVIRONMENTS.PROD]: {
        key: ENVIRONMENTS.PROD,
        name: "Prod",
        color: "#e03131",
        backgroundColor: "#ffc9c9",
        baseUrl: "https://api.ingrid.com/returns",
        host: "https://api.ingrid.com/returns/backoffice/api/v1",
        newHost: "https://api.ingrid.com/returns/backoffice",
        returnsBaseUrl: "https://returns.ingrid.com",
        demoBaseUrl: "https://returns-demo.ingrid.com",
        // Removed: returns.turnr.se and demo.turnr.se (dead URLs)
        oidcAuth: {
            authority: "https://signin.ingrid.com",
            clientId: "360438128839822611",
            clientIdLocalhost: "369987181286001212", // We use a special client ID when running prod locally because of security discussion with the platform team
        },
    },
    [ENVIRONMENTS.STAGE]: {
        key: ENVIRONMENTS.STAGE,
        name: "Stage",
        color: "#e67700",
        backgroundColor: "#ffec99",
        baseUrl: "https://api-stage.ingrid.com/returns",
        host: "https://api-stage.ingrid.com/returns/backoffice/api/v1",
        newHost: "https://api-stage.ingrid.com/returns/backoffice",
        returnsBaseUrl: "https://returns-stg.ingrid.com",
        demoBaseUrl: "https://returns-demo.ingrid.com",
        oidcAuth: {
            authority: "https://signin-stg.ingrid.com",
            clientId: "371457919234867283",
        },
    },
    [ENVIRONMENTS.DEV]: {
        key: ENVIRONMENTS.DEV,
        name: "Dev",
        color: "#5c940d",
        backgroundColor: "#d8f5a2",
        baseUrl: "https://api-development.ingrid.com/returns",
        host: "https://api-development.ingrid.com/returns/backoffice/api/v1",
        newHost: "https://api-development.ingrid.com/returns/backoffice",
        returnsBaseUrl: "https://returns-dev.ingrid.com",
        demoBaseUrl: "https://returns-demo.ingrid.com",
        oidcAuth: {
            authority: "https://signin-dev.ingrid.com",
            clientId: "360303842190950676",
        },
    },
    [ENVIRONMENTS.LOCALHOST]: {
        key: ENVIRONMENTS.LOCALHOST,
        name: "Localhost",
        color: COLORS.INFO,
        backgroundColor: "#a5d8ff",
        baseUrl: `http://localhost:${rasputinPort}`,
        host: `http://localhost:${rasputinPort}/backoffice/api/v1`,
        newHost: `http://localhost:${rasputinPort}/backoffice`,
        returnsBaseUrl: `http://localhost:${returnPortalPort}`,
        demoBaseUrl: `http://localhost:${returnPortalPort}?env=DEMO_APP`,
        // OIDC for localhost is resolved at runtime via /ping — see getOidcConfig in oidc.js
    },
};

// --- Environment detection (used by both apps) ---

export function isLocalDev() {
    return process.env.NODE_ENV === "development";
}

function getCurrentBrowserHostname() {
    return typeof window !== "undefined" ? window.location.hostname : "";
}

function isDevHostname(hostname) {
    if (!hostname || hostname.includes("-prod.")) return false;
    return hostname.includes("-dev.") || hostname.includes("-development.") || hostname.startsWith("dev.");
}

function isStageHostname(hostname) {
    if (!hostname || hostname.includes("-prod.")) return false;
    return hostname.includes("-stg.") || hostname.includes("-stage.") || hostname.startsWith("stage.");
}

function hostPartOf(url) {
    const s = String(url ?? "");
    // A leading slash means we were handed a path, not a host.
    if (s.startsWith("/")) return "";
    try {
        // SSR passes "req.headers.host + resolvedUrl", which has no scheme. Match a scheme
        // only at the start, so a "://" inside a query value isn't mistaken for one.
        return new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(s) ? s : "https://" + s).hostname;
    } catch {
        return "";
    }
}

// --- Return portal: SSR host detection based on request URL ---

// Used in return portal only: detects which backend environment a request URL targets
export function isDemoEnvironment() {
    const env = process.env.NEXT_PUBLIC_TURNR_ENV;
    return env === "demo-prod" || env === "demo-stage" || env === "demo-dev" || env === "demo-other";
}

export function isServerDevHost(url) {
    return isDevHostname(hostPartOf(url));
}

export function isServerStageHost(url) {
    return isStageHostname(hostPartOf(url));
}

export function getServerHostKey(url) {
    const urlParams = new URLSearchParams("?" + url?.split("?")[1]);
    const hostInUrl = urlParams.get("host");
    if (hostInUrl) return hostInUrl;
    if (isServerDevHost(url)) return ENVIRONMENTS.DEV;
    if (isServerStageHost(url)) return ENVIRONMENTS.STAGE;
    return ENVIRONMENTS.PROD;
}

export function getServerHostUrl(url) {
    return HOSTS[getServerHostKey(url)]?.baseUrl;
}

// Used in return portal only: formats HOSTS as styled dropdown options (label = baseUrl)
export function getHostSelectOptions() {
    return Object.values(HOSTS).map((host) => ({
        ...host,
        label: host.baseUrl,
        value: host.key,
        style: { backgroundColor: host.backgroundColor, color: host.color, fontWeight: "bold" },
    }));
}

// --- Backoffice: deployed environment helpers ---

// Used in backoffice only: returns the HOSTS entry matching the current deployed environment
export function getDeployedHost() {
    const hostname = getCurrentBrowserHostname();
    if (isDevHostname(hostname)) return HOSTS[ENVIRONMENTS.DEV];
    if (isStageHostname(hostname)) return HOSTS[ENVIRONMENTS.STAGE];
    return HOSTS[ENVIRONMENTS.PROD];
}

// Used in backoffice only: true when running on a deployed dev env or locally
export function isDevOrLocalEnv() {
    return isDevHostname(getCurrentBrowserHostname()) || isLocalDev();
}

// Used in backoffice only: looks up a HOSTS entry by environment key (e.g. "DEV", "PROD")
export function getHost(key) {
    return HOSTS[key];
}
